import * as dateFNS from 'date-fns';
import ptBR from 'date-fns/locale/pt-BR';

class DateFnsService {
  usDateToDate(dateString: string): Date {
    return new Date(`${dateString}T03:00:00`);
  }

  dateAndTimeToDate(date: string, time: string): Date {
    return new Date(`${date}T${time}`);
  }

  format(date: Date | string, pattern: string) {
    return dateFNS.format(this.ensureValidDate(date as Date), pattern, {
      locale: ptBR,
    });
  }

  dateTimeFormat(date: Date | string) {
    return dateFNS.format(
      this.ensureValidDate(date as Date),
      'dd/MM/yyyy HH:mm',
    );
  }

  dateTimeFormatWithDistance(date: Date | string) {
    return `${dateFNS.format(
      this.ensureValidDate(date as Date),
      'dd/MM/yyyy HH:mm',
    )} (${this.formatDistanceFromNow(this.ensureValidDate(date as Date))})`;
  }

  parse(dateString: string, format: string) {
    return dateFNS.parse(dateString, format, new Date());
  }

  dateFormat(date: Date | string) {
    return dateFNS.format(this.ensureValidDate(date as Date), 'dd/MM/yyyy');
  }

  usDateFormat(date: Date | string) {
    return dateFNS.format(this.ensureValidDate(date as Date), 'yyyy-MM-dd');
  }

  usDateToDateFormat(date: Date | string) {
    if (dateFNS.isValid(new Date(`${date}T00:00:00`))) {
      return dateFNS.format(new Date(`${date}T00:00:00`), 'dd/MM/yyyy');
    }
    return dateFNS.format(new Date(date), 'dd/MM/yyyy');
  }

  apiDateFormat(date: Date | string) {
    return dateFNS.format(this.ensureValidDate(date as Date), 'yyyy-MM-dd');
  }

  timeFormat(date: Date | string) {
    return dateFNS.format(this.ensureValidDate(date as Date), 'HH:mm:ss');
  }

  isAfter(leftDate: Date, rightDate: Date) {
    return dateFNS.isAfter(
      this.ensureValidDate(leftDate),
      this.ensureValidDate(rightDate),
    );
  }

  isBeforeFromNow(date: Date) {
    return dateFNS.isBefore(new Date(), this.ensureValidDate(date));
  }

  isAfterFromNow(date: Date) {
    return dateFNS.isAfter(new Date(), this.ensureValidDate(date));
  }

  isBefore(leftDate: Date, rightDate: Date) {
    return dateFNS.isBefore(
      this.ensureValidDate(leftDate),
      this.ensureValidDate(rightDate),
    );
  }

  addMinutes(date: Date, minutes: number) {
    return dateFNS.addMinutes(this.ensureValidDate(date), minutes);
  }

  addDays(date: Date, days: number) {
    return dateFNS.addDays(this.ensureValidDate(date), days);
  }

  addSeconds(date: Date, seconds: number) {
    return dateFNS.addSeconds(this.ensureValidDate(date), seconds);
  }

  addHours(date: Date, hours: number) {
    return dateFNS.addHours(this.ensureValidDate(date), hours);
  }

  addMonths(date: Date, quantity: number) {
    return dateFNS.addMonths(this.ensureValidDate(date), quantity);
  }

  subMonths(date: Date, quantity: number) {
    return dateFNS.subMonths(this.ensureValidDate(date), quantity);
  }

  subDays(date: Date, quantity: number) {
    return dateFNS.subDays(this.ensureValidDate(date), quantity);
  }

  subHours(date: Date, quantity: number) {
    return dateFNS.subHours(this.ensureValidDate(date), quantity);
  }

  isValid(date: Date): boolean {
    return dateFNS.isValid(date);
  }

  isStringDate(date: string): boolean {
    return dateFNS.isValid(new Date(date));
  }

  ensureValidDate(date: Date): Date {
    return this.isValid(date) ? date : new Date(date);
  }

  isWithinInterval(date: Date, interval: {start: Date; end: Date}): boolean {
    return dateFNS.isWithinInterval(this.ensureValidDate(date), {
      start: this.ensureValidDate(interval.start),
      end: this.ensureValidDate(interval.end),
    });
  }

  differenceInHours(leftDate: Date, rightDate: Date) {
    return dateFNS.differenceInHours(
      this.ensureValidDate(leftDate),
      this.ensureValidDate(rightDate),
    );
  }

  differenceInMinutes(leftDate: Date, rightDate: Date) {
    return dateFNS.differenceInMinutes(
      this.ensureValidDate(leftDate),
      this.ensureValidDate(rightDate),
    );
  }

  differenceInSeconds(leftDate: Date, rightDate: Date) {
    return dateFNS.differenceInSeconds(
      this.ensureValidDate(leftDate),
      this.ensureValidDate(rightDate),
    );
  }

  differenceInMinutesFromNow(date: Date) {
    return dateFNS.differenceInMinutes(new Date(), this.ensureValidDate(date));
  }

  differenceInSecondsFromNow(date: Date) {
    return dateFNS.differenceInSeconds(new Date(), this.ensureValidDate(date));
  }

  startOfDay(date: Date) {
    return dateFNS.startOfDay(this.ensureValidDate(date));
  }

  endOfDay(date: Date) {
    return dateFNS.endOfDay(this.ensureValidDate(date));
  }

  getDaysInMonth(date: Date) {
    return dateFNS.getDaysInMonth(this.ensureValidDate(date));
  }

  formatDistanceFromNow(date: Date) {
    return dateFNS.formatDistance(this.ensureValidDate(date), new Date(), {
      addSuffix: true,
      locale: ptBR,
    });
  }

  getMonth(date: Date = new Date()) {
    return dateFNS.getMonth(this.ensureValidDate(date));
  }

  getYear(date: Date = new Date()) {
    return dateFNS.getYear(this.ensureValidDate(date));
  }

  apiDateFormatToDate(date: Date | string) {
    return new Date(`${date}T03:00:00.000Z`);
  }

  isSameDay(leftDate: Date, rightDate: Date) {
    return dateFNS.isSameDay(
      this.ensureValidDate(leftDate),
      this.ensureValidDate(rightDate),
    );
  }
}

export default new DateFnsService();
